import { useEffect, useRef, type ReactNode } from 'react';

import {
  animateSpring,
  project,
  SPRING_DEFAULT,
  VelocityTracker,
  type SpringAnimation,
} from '@/lib/motion';

/** Ширина зоны у левого края, с которой начинается жест «назад». */
const EDGE = 28;
/** Порог, после которого палец ведёт экран за собой, а не листает содержимое. */
const THRESHOLD = 10;
/** Бросок от этой скорости закрывает экран, даже если утянули недалеко. */
const FLING = 400;

/**
 * Возврат смахиванием от левого края — как в родных приложениях.
 *
 * Внутри Telegram «назад» есть только в шапке клиента, и рука всё равно тянется смахнуть.
 * Экран идёт за пальцем 1:1, отпускание решает по расстоянию и скорости: больше трети ширины
 * или бросок — уходим назад, иначе экран возвращается пружиной. Жест берётся только у самого
 * края, чтобы не спорить с прокруткой, каруселями и шторкой.
 */
export function SwipeBack({ enabled, onBack, children }: { enabled: boolean; onBack: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const tracker = new VelocityTracker();
    let animation: SpringAnimation | null = null;
    let startX = 0;
    let startY = 0;
    let dragging = false;
    let armed = false;
    let pointerId: number | null = null;

    const paint = (x: number) => {
      el.style.transform = x === 0 ? '' : `translate3d(${x}px, 0, 0)`;
      el.style.boxShadow = x > 0 ? '-12px 0 24px rgba(0,0,0,0.35)' : '';
    };

    const settle = (from: number, velocity: number, to: number, done?: () => void) => {
      animation = animateSpring({
        from: { value: from, velocity },
        to,
        config: SPRING_DEFAULT,
        onUpdate: paint,
        onComplete: () => {
          animation = null;
          done?.();
        },
      });
    };

    const onDown = (e: PointerEvent) => {
      if (e.pointerType === 'mouse' || e.clientX > EDGE) return;
      animation?.stop();
      animation = null;
      armed = true;
      dragging = false;
      startX = e.clientX;
      startY = e.clientY;
      pointerId = e.pointerId;
      tracker.reset();
      tracker.push(0, e.timeStamp);
    };

    const onMove = (e: PointerEvent) => {
      if (!armed || e.pointerId !== pointerId) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!dragging) {
        // Вертикальное движение отдаём прокрутке: жест назад — только горизонтальный
        if (Math.abs(dy) > Math.abs(dx)) {
          armed = false;
          return;
        }
        if (dx < THRESHOLD) return;
        dragging = true;
        el.setPointerCapture(e.pointerId);
      }
      const x = Math.max(0, dx);
      tracker.push(x, e.timeStamp);
      if (!reduced) paint(x);
      e.preventDefault();
    };

    const finish = (e: PointerEvent) => {
      if (!armed || e.pointerId !== pointerId) return;
      const wasDragging = dragging;
      armed = false;
      dragging = false;
      pointerId = null;
      if (!wasDragging) return;

      const x = Math.max(0, e.clientX - startX);
      const velocity = tracker.velocity(e.timeStamp);
      const width = el.offsetWidth || window.innerWidth;
      const projected = x + project(velocity);
      const goBack = velocity > FLING || projected > width / 3;

      if (!goBack) {
        settle(x, velocity, 0);
        return;
      }
      if (reduced) {
        paint(0);
        onBack();
        return;
      }
      // Экран уходит вправо тем же движением, и только потом меняется маршрут: иначе новый
      // экран появлялся бы под сдвинутым старым.
      settle(x, velocity, width, () => {
        paint(0);
        onBack();
      });
    };

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove, { passive: false });
    el.addEventListener('pointerup', finish);
    el.addEventListener('pointercancel', finish);
    return () => {
      animation?.stop();
      paint(0);
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', finish);
      el.removeEventListener('pointercancel', finish);
    };
  }, [enabled, onBack]);

  return (
    <div ref={ref} className="min-h-screen touch-pan-y">
      {children}
    </div>
  );
}
